// Rich text is stored as a portable plaintext string with inline mention
// tokens: `@[Label](entityId)`. The label is a snapshot (so a deleted entity
// still reads sensibly) and the id is the durable link. This keeps every
// description/note/comment greppable and readable outside Crum while still
// letting the app render live links and resolve the current name.

export interface TextSegment {
  type: 'text'
  text: string
}
export interface MentionSegment {
  type: 'mention'
  id: string
  label: string
}
export type Segment = TextSegment | MentionSegment

// The id half allows underscores, so hand-authored or imported ids can be readable
// slugs (`prj_kf_auth`, `prj_demo_jocn`) rather than only generated ULIDs. The
// closing paren anchors the match, so this stays unambiguous. `kindOfId` reads the
// prefix up to the FIRST underscore, which is what keeps slugs working everywhere.
const MENTION_RE = /@\[([^\]\n]+)\]\(([a-z]+_[0-9A-Za-z_-]+)\)/g

export function parseBody(body: string): Segment[] {
  const segments: Segment[] = []
  let last = 0
  MENTION_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = MENTION_RE.exec(body)) !== null) {
    if (m.index > last) segments.push({ type: 'text', text: body.slice(last, m.index) })
    segments.push({ type: 'mention', label: m[1]!, id: m[2]! })
    last = m.index + m[0].length
  }
  if (last < body.length) segments.push({ type: 'text', text: body.slice(last) })
  return segments
}

export function mentionToken(id: string, label: string): string {
  // strip characters that would break the token syntax, collapse whitespace
  const clean =
    label
      .replace(/[[\]\n]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() || id
  return `@[${clean}](${id})`
}

/** All entity ids referenced by a body — for backlinks / "mentioned in". */
export function mentionedIds(body: string): string[] {
  const ids: string[] = []
  for (const seg of parseBody(body)) if (seg.type === 'mention') ids.push(seg.id)
  return ids
}

/** A mention, plus the sentence it sits in. */
export interface MentionInContext {
  id: string
  label: string
  /** the sentence around it, mentions flattened to their labels */
  sentence: string
}

/**
 * Every mention in a body with the sentence that frames it.
 *
 * The sentence is the point: "Grew out of @[Union catalogue](prj_x)" says both WHAT
 * the connection is and WHY, and it was written by a person in prose rather than
 * picked from a dropdown. A derived connection carries it as its label, so the graph
 * reads back in the words the record already used.
 */
export function mentionsWithContext(body: string): MentionInContext[] {
  // Offsets are into the FLATTENED text, so a sentence reads the way a person sees
  // it on the page rather than with `@[...](...)` tokens in the middle of it.
  let plain = ''
  const marks: { id: string; label: string; at: number }[] = []
  for (const seg of parseBody(body)) {
    if (seg.type === 'text') plain += seg.text
    else {
      marks.push({ id: seg.id, label: seg.label, at: plain.length })
      plain += seg.label
    }
  }
  return marks.map(({ id, label, at }) => ({
    id,
    label,
    sentence: sentenceAround(plain, at, at + label.length),
  }))
}

const BOUNDARY = /[.!?\n]/

/** The sentence spanning [from, to). Scanning forward starts AFTER the mention so a
 *  label with a full stop in it ("Collections vocabulary, v4.") is not a boundary. */
function sentenceAround(plain: string, from: number, to: number): string {
  let start = 0
  for (let i = from - 1; i >= 0; i--) {
    if (BOUNDARY.test(plain[i]!)) {
      start = i + 1
      break
    }
  }
  let end = plain.length
  for (let i = to; i < plain.length; i++) {
    if (BOUNDARY.test(plain[i]!)) {
      end = i + 1
      break
    }
  }
  return plain.slice(start, end).trim()
}

/** Plain text with mentions flattened to their labels (for search / previews). */
export function bodyToPlain(body: string): string {
  return parseBody(body)
    .map((s) => (s.type === 'text' ? s.text : s.label))
    .join('')
}
